import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TrustedSourceItem } from "@/api/models"

import { CheckSquare, Search, Square } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useGetTrustedSources } from "@/features/trusted-sources/hooks/use-get-trusted-sources"
import { cn } from "@/lib/utils"

interface TrustedSourcesPickerProps {
  selectedUrls: string[]
  trustedMode: "only" | "preferred"
  onToggleSource: (url: string) => void
  onModeChange: (mode: "only" | "preferred") => void
}

interface SourceGroupProps {
  sources: TrustedSourceItem[]
  selectedUrls: string[]
  onToggleSource: (url: string) => void
}

function SourceGroup({ sources, selectedUrls, onToggleSource }: SourceGroupProps) {
  return (
    <>
      {sources.map((source) => {
        const isActive = selectedUrls.includes(source.url)
        return (
          <button
            key={source.id}
            type="button"
            className={cn(
              "focus-visible:ring-ring flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none",
              isActive ? "bg-success/15 ring-success ring-1" : "hover:bg-muted/50"
            )}
            onClick={() => onToggleSource(source.url)}
          >
            <span
              className={cn("mt-0.5 shrink-0", isActive ? "text-success" : "text-muted-foreground")}
            >
              {isActive ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{source.name}</div>
              <div className="text-muted-foreground line-clamp-1 text-xs">{source.url}</div>
            </div>
          </button>
        )
      })}
    </>
  )
}

export function TrustedSourcesPicker({
  selectedUrls,
  trustedMode,
  onToggleSource,
  onModeChange,
}: TrustedSourcesPickerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.agentNode" })
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const { data: trustedSourcesData } = useGetTrustedSources()
  const allTrustedSources = trustedSourcesData?.sources || []

  const personalTrustedSources = useMemo(
    () => allTrustedSources.filter((s) => s.type === "personal"),
    [allTrustedSources]
  )
  const companyTrustedSources = useMemo(
    () => allTrustedSources.filter((s) => s.type === "company"),
    [allTrustedSources]
  )

  const filteredTrustedSources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return allTrustedSources
    return allTrustedSources.filter(
      (source) =>
        source.name.toLowerCase().includes(query) || source.url.toLowerCase().includes(query)
    )
  }, [allTrustedSources, searchQuery])

  const filteredPersonalSources = useMemo(
    () => personalTrustedSources.filter((s) => filteredTrustedSources.some((fs) => fs.id === s.id)),
    [personalTrustedSources, filteredTrustedSources]
  )
  const filteredCompanySources = useMemo(
    () => companyTrustedSources.filter((s) => filteredTrustedSources.some((fs) => fs.id === s.id)),
    [companyTrustedSources, filteredTrustedSources]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn("nodrag -m-0.5 size-6 p-0", selectedUrls.length > 0 && "text-success")}
          size="icon"
          variant="ghost"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center">
                <span className="h-3 w-3">🛡️</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {t("trustedSourcesPicker.configureSources")}
                {selectedUrls.length > 0 && (
                  <span className="ml-1 opacity-70">({selectedUrls.length})</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="nodrag nowheel w-[320px] p-0"
        side="right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2 left-2 h-4 w-4" />
            <Input
              className="h-8 pl-8 text-sm"
              placeholder={t("trustedSourcesPicker.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="border-b px-2 pb-2">
          <Label className="mb-1 block text-xs font-medium">{t("trustedSourcesPicker.mode")}</Label>
          <div className="flex gap-1">
            <button
              type="button"
              className={cn(
                "focus-visible:ring-ring rounded border border-transparent px-2 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none",
                trustedMode === "only"
                  ? "bg-success/15 border-success text-foreground border"
                  : "hover:bg-muted/50"
              )}
              onClick={() => onModeChange("only")}
            >
              {t("trustedSourcesPicker.modeOnly")}
            </button>
            <button
              type="button"
              className={cn(
                "focus-visible:ring-ring rounded border border-transparent px-2 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none",
                trustedMode === "preferred" ? STATUS_TONES.info.solid : "hover:bg-muted/50"
              )}
              onClick={() => onModeChange("preferred")}
            >
              {t("trustedSourcesPicker.modePreferred")}
            </button>
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto p-2">
          {filteredTrustedSources.length === 0 ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              {t("trustedSourcesPicker.noSources")}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredPersonalSources.length > 0 && (
                <>
                  <div className="text-muted-foreground px-2 pt-1 text-[10px] font-medium uppercase">
                    {t("trustedSourcesPicker.personalSources")}
                  </div>
                  <SourceGroup
                    selectedUrls={selectedUrls}
                    sources={filteredPersonalSources}
                    onToggleSource={onToggleSource}
                  />
                </>
              )}
              {filteredCompanySources.length > 0 && (
                <>
                  <div className="text-muted-foreground px-2 pt-2 text-[10px] font-medium uppercase">
                    {t("trustedSourcesPicker.companySources")}
                  </div>
                  <SourceGroup
                    selectedUrls={selectedUrls}
                    sources={filteredCompanySources}
                    onToggleSource={onToggleSource}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
